import React, { BaseSyntheticEvent, useMemo, useState } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { Form } from '@components/Form';
import { useFormLoader } from '@components/Form/Form.hooks';
import { EnquiryFormData } from '@modules/EnquiryForm/EnquiryForm.types';
import {
  SubmitFormResponse,
  useSubmitEnquiryFormMutation,
} from '@modules/EnquiryForm/EnquiryForm.service';
import { useOnSubmitError, useParentPageState } from './EnquiryTypeForm.hooks';
import { createFormData, getFormDefaultValues } from './EnquiryTypeForm.helpers';
import './EnquiryTypeForm.scss';

interface EnquiryTypeFormProps {
  formData: EnquiryFormData;
  type: string;
  dropdownToDisable?: string;
  pageId?: string;
  parentPageID: string;
  i18n: Record<string, string>;
  onFormSubmit: (data: SubmitFormResponse, formValues: EnquiryForm, e: BaseSyntheticEvent) => void;
}

export type EnquiryForm = Partial<{
  Comments: string;
  ImageUpload: File[];
  recaptcha: string;
  Name: string;
  Email: string;
  Phone: string;
  Company: string;
  Country: string;
  City: string;
  Address: string;
  PostalCode: string;
  SerialNr: string[];
  Industry: string;
  Application: string;
  Product: string;
  Region: string;
  ServiceType: string;
  Toggle: boolean;
  GDPR: boolean;
  selectedProductNames: string[];
}>;

export const EnquiryTypeForm = ({
  formData,
  i18n,
  onFormSubmit,
  dropdownToDisable,
  type,
  pageId,
  parentPageID,
}: EnquiryTypeFormProps) => {
  const [formSubmitError, setFormSubmitError] = useOnSubmitError();
  const [doFormSubmit] = useSubmitEnquiryFormMutation();
  const [firstSuccess, setFirstSuccess] = useState(false);
  const defaultValues = getFormDefaultValues(formData);
  const formFields = Object.keys(defaultValues);

  const throughModel =
    formData.FormSections[0].FormField[0].DataType === 'QuoteItem' ? 'through-modal' : '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm({
    reValidateMode: 'onBlur',
    defaultValues,
    shouldFocusError: false,
  });
  const gdpr = watch('GDPR');
  const hasToggleField = useMemo(
    () =>
      formData?.FormSections?.some((section) =>
        section.FormField.find((field) => field.DataType === 'Toggle')
      ),
    [formData]
  );

  const { parentPageId, parentPageUrl } = useParentPageState();
  //To render the form field elements based on the type - Dynamic elements
  const loadFieldElements = useFormLoader(
    register,
    watch,
    setValue,
    trigger,
    hasToggleField,
    isSubmitted === true,
    dropdownToDisable
  );

  const focusOnFirstError = () => {
    const firstFieldWithError = formFields.find((field) => errors[field]);
    const firstFieldWithErrorElement: HTMLElement = document.querySelector(
      `[name=${firstFieldWithError}]`
    );

    firstFieldWithErrorElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    firstFieldWithErrorElement?.focus();
  };

  const onSubmit: SubmitHandler<EnquiryForm> = async (data, event) => {
    event.stopPropagation();
    const recaptchaToken = await window.onLoadContact();

    const { formDataPayload, formValues } = createFormData(
      type,
      data,
      recaptchaToken,
      {
        Id: parentPageId,
        Url: parentPageUrl,
      },
      i18n,
      parentPageID
    );

    if (pageId) {
      formDataPayload.delete('PageId');
      formDataPayload.append('PageId', pageId);
    }

    return doFormSubmit(formDataPayload)
      .unwrap()
      .then((result) => {
        if (!(result && result.success)) {
          setFormSubmitError(result?.message || true);
        } else {
          setFirstSuccess(true); //To disable send button after 1st successful api request
          onFormSubmit(result, formValues, event);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onFormValidationError = (errors: FieldErrors<EnquiryForm>) => {
    focusOnFirstError();

    if (hasToggleField && errors?.SerialNr) {
      setValue('Toggle', true, { shouldValidate: false });
    }
  };
  
  return (
    <div className={`c-contact-enquiry-right ${throughModel}`}>
      <Form
        formData={formData}
        sendBtnLabel={i18n.SendButtonLabel}
        isSubmitDisabled={gdpr === false || firstSuccess}
        fieldLoader={loadFieldElements}
        onSubmit={handleSubmit(onSubmit, onFormValidationError)}
        errors={errors}
        formError={formSubmitError}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};
